import type { With, MilestoneType, WithMore, ConciseWaveType, WaveSpanType } from './types'

export function groupMilestones(
  milestonesArray: MilestoneType[],
  rowsCount: number
): With<WithMore<MilestoneType>[], { key: string }>[] {
  const match = milestonesArray.filter(milestone => milestone.offset >= 0 && milestone.offset < 1)

  match.sort((a, b) => a.offset - b.offset)

  const rows = match.reduce(
    (rows, milestone) => {
      for (let i = 0; i < rowsCount; i++) {
        if (!rows[i]) {
          rows[i] = [] as With<WithMore<MilestoneType>[], { key: string }>
        }

        const lastElement = rows[i][rows[i].length - 1]
        const isElementTooCloseToPrevious = lastElement
          ? lastElement.offset + lastElement.titleSpan > milestone.offset ||
            lastElement.offset + lastElement.titleSpan > 1 - milestone.titleSpan
          : false
        const isLastRow = i === rowsCount - 1
        const isElementOverhanging = milestone.offset + milestone.titleSpan > 1
        if (isElementOverhanging && !isElementTooCloseToPrevious) {
          const replacement = Object.assign({ ...milestone }, { offset: 1 - milestone.titleSpan })
          rows[i].push(replacement)
          break
        }
        if (isElementTooCloseToPrevious && isLastRow) {
          if (!lastElement.more) {
            lastElement.more = []
          }
          lastElement.more.push(milestone)
          break
        } else if (!isElementTooCloseToPrevious) {
          rows[i].push(milestone)
          break
        }
      }
      return rows
    },
    [[]] as With<WithMore<MilestoneType>[], { key: string }>[]
  )

  rows.forEach(row => Object.assign(row, { key: row.map(m => m.id).join('-') }))

  return rows
}

export const spanToGridColumn = (start: number, span?: number, base: number = 100) => {
  return {
    left: `${Math.round(start * base)}px`,
    ...('number' === typeof span ? { width: `${Math.max(3, Math.round(span * base))}px` } : {})
  }
}

export function getNormalizedWaves(waves: ConciseWaveType[], min: number, max: number): WaveSpanType[] {
  const normalizeBase = max - min
  return waves
    .filter(wave => wave.to >= min && (wave.from <= max || wave.to + (wave.titleSpan ?? 0) <= max))
    .map(wave => {
      return {
        ...wave,
        start: (Math.max(wave.from, min) - min) / normalizeBase,
        span: (Math.min(wave.to, max) - Math.max(wave.from, min)) / normalizeBase,
        title: wave.title,
        id: wave.uid
      }
    })
}

export function getBounds(source: ConciseWaveType[]) {
  return source.reduce(
    ({ min, max }, wave) => {
      return {
        min: Math.min(min, wave.from),
        max: Math.max(max, wave.to, wave.from + (wave.titleSpan ?? 0))
      }
    },
    {
      min: Infinity,
      max: 0
    }
  )
}

type KeyedWaveSpanType = WaveSpanType[] & { key: string }
export function packIntoRows(waves: WaveSpanType[]): KeyedWaveSpanType[] {
  const rows: KeyedWaveSpanType[] = []
  waves.forEach(wave => {
    for (let i = 0; i < rows.length; i++) {
      if (!rows[i]) {
        rows[i] = [] as KeyedWaveSpanType
        rows[i].key = ''
      }
      if (
        rows[i].every(
          w =>
            (w.to < wave.from && w.from + (w.titleSpan ?? 0) < wave.from && w.from < wave.from) ||
            (w.from >= wave.to &&
              w.from >= wave.from + (wave.titleSpan ?? 0) &&
              w.to > wave.to &&
              w.from + (w.titleSpan ?? 0) > wave.from + (wave.titleSpan ?? 0))
        )
      ) {
        rows[i].push(wave)
        rows[i].key += `-${wave.uid}-${wave.titleSpan}`
        wave.row = i
        return
      }
    }
    wave.row = rows.length
    rows[rows.length] = Object.assign([wave], { key: `-${wave.uid}-${wave.titleSpan}` })
  })
  return rows
}
